/*
 * @Author:
 * @Date: 2023-12-22 13:58:05
 * @LastEditors: Merlin
 * @LastEditTime: 2025-08-20 13:35:08
 * @Description: 创建 websocket 链接实例
 */
import { io, Socket } from 'socket.io-client';
import { enCodeToken, getServerSideTK, deCodeToken, destroyToken } from '~/stores/auth';
import useGlobalStore from '@/stores/global'; 
import Cookies from 'js-cookie';
import { pendingEvents } from './socket';

interface MySocket extends Socket {
    updateToken?: (newToken: string) => void;
}

let oSocketInstance: MySocket;
// ↓ 状态码说明 2024.01.24 18:23:12 星期三
// ↓ 后端 Joe： token 如果失效或者验证有问题，会返回这个错误码 1003 👈
// ↓ 前端要处理下，这种情况需要销毁原有 token 让用户重新登录。
const tokenWrongCode = '1003';
const isDev = import.meta.dev;
const isClient = import.meta.client;
let iCounter4Connect = 0;

// 创建 websocket 链接实例
export const socket = ((): MySocket => {
    try {
        console.log("ws initialization started");
        // ↓ 防止测试环境频繁刷新
        if (oSocketInstance) return oSocketInstance;
        const url = (
            (import.meta.dev || import.meta.env.DEV_MODE)
            ? import.meta.env.VITE_SOCKET_URL // 前后端共享地址 
            // ↓ 用于生产环境，浏览器一侧不指定地址 
            : (import.meta.server ? import.meta.env.VITE_SOCKET_URL : '')
        );
        const path = (
            (import.meta.env.PROD && import.meta.client)
            ? import.meta.env.VITE_SOCKET_PATH // 生产环境 && 前端浏览器一侧，专用 
            : void 0
        );
        console.log('PROD Mark', import.meta.env.PROD);
        console.log('DEV_MODE', import.meta.env.DEV_MODE);
        if (isClient && isDev) {
            console.log('webSocket 收到了服务器下发的 TK 值 ↓\n', getServerSideTK() || '无'); 
        }
        oSocketInstance = io(url, {
            reconnection: true,            // 启用重连
            reconnectionAttempts: 10,      // 最大重连尝试次数
            reconnectionDelay: 3_000,       // 每次重连的延迟时间（5秒），后改为3秒，以便及时地捕捉到2次重连操作，并弹出提示  
            reconnectionDelayMax: 10_000,   // 重连延迟的最大时间（10秒）
            timeout: 20_000,                // 连接超时时间（20秒）
            autoConnect: true,             // 自动尝试连接 
            transports: ['websocket', 'polling'], // 支持 WebSocket 和轮询降级
            // ↑ 以上是后端同事推荐设定，详见 https://t.me/c/2041945384/16725 （2024.12.16 16:53:9 星期一） 
            auth: {
                token: (
                    isClient
                    ? (getServerSideTK() || deCodeToken()) // 首选值是后端推到浏览器的 token 值 
                    : void 0
                ),
            },
            query: {
                locale: Cookies.get('locale') || Cookies.get('i18n_redirected'),
            },
            path, 
        });
        console.log('[ws] 首次创建 Promise');
        oSocketInstance['oTokenPromise_'] = createPromise();
        // ↓ 更新 token 的方法 
        // ↓ 新 token 有多种来源 1： 旧 token 过期，后端发来新的替换旧的 
        // ↓ 新 token 有多种来源 2： 收到 au 值，用 au token 替换旧的
        oSocketInstance.updateToken = async (sNewToken: string) => {
            console.log('[ws] 开始执行updateToken, 新token:', sNewToken?.slice(0, 10) + '...');
            const isSameToken = oSocketInstance.auth['token'] == sNewToken;
            if (isSameToken) {
                console.log('[ws] Token相同，无需更新');
                return;
            }
            // ↑ 这项判断稍显多余，暂时保留无妨 
            console.log('[ws] 准备断开当前连接');
            oSocketInstance.disconnect();
            console.log('[ws] 更新token');
            oSocketInstance.auth['token'] = sNewToken;
            console.log('[ws] 开始重新连接');
            oSocketInstance.connect();
            console.log("[ws] updateToken执行完毕，等待Promise结果");
            return oSocketInstance['oTokenPromise_'].promise;
        };
        Promise.resolve().then(() => {
            // ↓ 监测所有通信内容，捕捉特定错误码 
            // ↓ 当收到特殊 code 时，提示用户重新登录 
            function checkPacket(engine) {
                // ↓ 官方文档： called for each packet received 
                engine.on("packet", ({ type, data = '' }) => {
                    // ☢️☢️☢️ 在此方法内启用打印将导致打印相当频繁，不要轻易开启 
                    // console.log('engine.on-packet') 
                    // type 值示例： message | ping | ****
                    // data 值示例： 422["gamelist.getOriginalGames",{}] 
                    // data 值示例： 422[{"error":"请求失败","code":1000}] 
                    const sFirstPart = data.slice(0, 100);
                    let sData = '';
                    let oData = null;
                    if (
                        sFirstPart.includes(`"error":`) ||
                        sFirstPart.includes(tokenWrongCode)
                    ) {
                        sData = (data.match(/\[.+\]/) || [])[0];
                    }
                    try {
                        const arr = JSON.parse(sData || '[]') || [];
                        oData = arr[0];
                    } catch (err) {
                        console.log("未能解析：", err);
                    }
                    // console.log('oData', oData);
                    if (!oData) return;
                    // isDev && console.log("packet:", data);
                    if (oData.code === tokenWrongCode) {
                        destroyToken({
                            showTips: true,
                            reload: true,
                            target: oSocketInstance.auth['token'],
                        });
                    } else if (oData.error || oData.code) {
                        // 之前这里调用方法弹出提示，后来被取消。应在接口调用处按需手工弹出
                        console.error('oData.code + error:', oData.code, '+', oData.error);
                        console.error('original data', data);
                        const lastPending = pendingEvents.length ? pendingEvents[pendingEvents.length - 1] : null;
                        if (lastPending) {
                            console.error(`[ws] 最近请求的接口: ${lastPending.eventName}`, '入参:', lastPending.params);
                        }
                    }
                });
            }
            // ↓ 创建链接成功 
            oSocketInstance.on('connect', () => {
                iCounter4Connect++; // 每次重连都自增
                console.log("[ws] 连接成功，计数:", iCounter4Connect);
                // ↓ 触发特定事件，例如查询用户登录状态等 
                oSocketInstance['oTokenPromise_'].resolve(iCounter4Connect);
                if (!isClient) return;
                if (iCounter4Connect === 1) {
                    const engine = oSocketInstance.io.engine;
                    // ↓ 这里效仿官方示例，把此方法写在了 connect 事件内部 
                    checkPacket(engine);
                }else if(iCounter4Connect > 1 && import.meta.client){
                    const oGlobalStore = useGlobalStore();
                    oGlobalStore.wsConnection = true; 
                }
                console.log('[ws] 链接成功了 ✅', new Date().toLocaleTimeString());
            });
            // ↓ 链接断线了 
            oSocketInstance.on('disconnect', () => {
                console.log('[ws]状态 disconnect 触发, 链接计数：', iCounter4Connect); 
                // 在浏览器网络页面切换为离线模式时，这里会“立即” 执行到此，速度很快 
                // 但后续的重连成功并触发 connect 时不快，稍慢
                fnCheckIfOfflineAndShowTips({});
            });
            // ↓ 链接失败事件监听 
            // ↓ 页面加载时首次链接失败到执行触发此监听，
            // ↓ 首次链接成功后设备断网也会触发此监听， 
            oSocketInstance.on('connect_error', (error: any) => {
                error ||= {};
                fnCheckIfOfflineAndShowTips(error);
                console.log('\nconnect_error ↓\n', error);
                if (1 && import.meta.dev){
                    for (const [key, val] of Object.entries(error || {})){
                        console.log(key, val);
                    }
                }
            });
            // ↓ 通信出错（例如使用错误的 token 创建链接将导致这样的报错） 
            oSocketInstance.on('error', (error: any) => {
                console.log('ws error ⛔\n', error);
                // console.log('链接出错 =>', new Date(Date.now() + 1_000 * 60 * 60 * 8).toISOString());
                if (error?.code === tokenWrongCode) {
                    destroyToken({
                        showTips: true,
                        reload: true,
                        target: oSocketInstance.auth['token'],
                    });
                }
            });
            // ========= ↓ 注意，以下是客户端专用的部分 ↓ ==================================== 
            if (!isClient) return;
            // ↓ 收到新的 token 时需要重新链接
            // ↓ 这项监听有特殊性，只能放在这里，因为在链接创建时可能“立即”收到新 token，只有在此启动监听才能及时使用新 token 
            oSocketInstance.on(`token.new`, (oData) => {
                let sNewToken = oData?.token;
                if (!sNewToken) return;
                isDev && console.log("新 Token:", JSON.stringify(oData));
                // ↓ 如果在刷新时执行到此，此时 pinia 无法调用，因此使用 enCodeToken() 将新值写入到 localStorage 中 
                // ↓ 效用：稍后 useAuthStore() 创建时将从 localStorage 取到此新值用于初始化 
                enCodeToken(sNewToken, true);
                oSocketInstance.updateToken(sNewToken);
            });
        });
        return oSocketInstance;
    } catch (error) {
        console.log('webSocket.ts:', error)
    }

})();


let iOffLineAt = 0; // 记录上次断网时间 
// ↓ 在断网时弹出提示 
function fnCheckIfOfflineAndShowTips(oErr){
    if (import.meta.server){ 
        return;
    }
    const isOffLine = socket.disconnected || [
        // 以下是实测得出的断网的报错特征，逐步调整 2025.03.07 11:50:27 星期五 
        // oErr.context == void 0,
        oErr.type == 'TransportError',
        typeof oErr.description == 'object',
    ].every(Boolean);
    if (!isOffLine) return; 
    if (import.meta.dev){
        console.log('[ws] 链接断线了 🚫', new Date().toLocaleTimeString()); 
    }
    const oGlobalStore = useGlobalStore();
    oGlobalStore.wsConnection = false; 
    // ↓ 以下代码已废弃 （改用新的提示方案了）
    // ↓ 将来删除；注释于 2025.08.20 15:13:26 星期三 
    // const iNow = Date.now();
    // const iGap = iNow - iOffLineAt;
    // const bToShowTips = (
    //     (iGap < 30_000) // x秒内二次断网，弹出提示 
    //     && (iOffLineAt > 0) // 在第二次断网时才弹出提示 
    // );
    // iOffLineAt = iNow; // 及时记录断网时间 
    // if (!bToShowTips) return;
    // console.log('[ws] Offline gap second:', (iGap / 1000).toFixed(0));
    // ↓ 弹出提示 
    // showNegativeNotify({
    //     // position: 'top',
    //     message: '',
    //     i18n: 'commonInfo.badNetwork', 
    //     timeout: 6_000, // 逗留时长 
    // });
}

